import { Skeleton } from "antd"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import { NotificationContentCard } from "../../../components/notifications/NotificationContentCard"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { setCentreOpen } from "../../Notifications/redux/notification.slice.ts"
import { contentDestination } from "../../../components/notifications/contentDestination"
import type { RootState } from "../../../store.ts"
import { NOTIFICATION_PATHS } from "../../Notifications/api/services.ts"
import { notificationContent } from "../../Notifications/helpers/notificationContent.ts"
import { parseNotificationMessage } from "../../Notifications/helpers/parseNotificationMessage.ts"
import type {
  Notification,
  NotificationCountsPayload,
  NotificationCursor
} from "../../Notifications/types/notification.types.ts"
import { HOME_LIST_MAX_HEIGHT } from "./homeListHeight.ts"
import { homeCardStyle, homeColor, homeText } from "../home-theme.ts"
import { HomeSectionHeader } from "./HomeSectionHeader.tsx"

/**
 * Home's notifications widget.
 *
 * Renders the same `NotificationContentCard` the bell and the notification
 * centre do, at the compact density — the rail is narrower, not poorer, so
 * the project, the customer and the figures are all still here. "View all"
 * opens the shared centre rather than a second list of its own.
 */
export function DashboardNotifications() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const accessToken = useSelector((state: RootState) => state.auth.accessToken)
  const containerRef = useRef<HTMLDivElement>(null)
  const wsRef = useRef<WebSocket | null>(null)

  const [notifications, setNotifications] = useState<Notification[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [nextId, setNextId] = useState<NotificationCursor>(null)
  const [counts, setCounts] = useState<NotificationCountsPayload | null>(null)
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [errored, setErrored] = useState(false)

  const onLoadMore = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({ type: "fetch_more", after_id: nextId })
      )
    }
  }

  useEffect(() => {
    if (!hasMore) return
    const container = containerRef.current
    if (!container) return
    const hasNoScroll = container.scrollHeight <= container.clientHeight
    if (hasNoScroll && notifications.length > 0) onLoadMore()
  }, [notifications.length, hasMore])

  useEffect(() => {
    if (!accessToken) return

    const socket = new WebSocket(NOTIFICATION_PATHS.getNotifications(accessToken))
    wsRef.current = socket

    socket.onmessage = (event) => {
      const data = parseNotificationMessage(event.data)
      if (!data) return

      if (data.type === "initial_state" && data.notifications) {
        setNotifications(data.notifications)
        setHasMore(data.has_more || false)
        setNextId(data.next_cursor)
        setUnreadCount(data.unread_count ?? 0)
        if (data.counts) setCounts(data.counts)
        setLoading(false)
        setErrored(false)
      }

      if (data.type === "notifications_batch") {
        if (data.notifications) {
          setNotifications((prev) => [
            ...prev,
            ...(data.notifications as Notification[])
          ])
        }
        setNextId(data.next_cursor)
        setHasMore(data.has_more || false)
      }

      if (data.type === "error") {
        setLoading(false)
        setErrored(true)
      }
    }

    // A socket that never opens is an error state, not an empty account.
    socket.onerror = () => {
      setLoading(false)
      setErrored(true)
    }

    return () => socket.close()
  }, [accessToken])

  const openNotification = (notification: Notification) => {
    const content = notificationContent(notification)
    const href = contentDestination(content?.action?.target)
    if (href) navigate(href)
  }

  return (
    <section
      className="h-full flex flex-col"
      style={{ ...homeCardStyle, overflow: "hidden" }}
    >
      <HomeSectionHeader
        title={t("notifications.bell.heading", "Notifications")}
        caption={
          (counts?.unread ?? unreadCount) > 0
            ? t("notifications.bell.unreadCaption", "{{count}} unread", {
                count: counts?.unread ?? unreadCount
              })
            : undefined
        }
        action={
          <button
            type="button"
            data-testid="home-notifications-view-all"
            onClick={() => dispatch(setCentreOpen(true))}
            style={{
              border: "none",
              background: "none",
              padding: 0,
              cursor: "pointer",
              ...homeText.button,
              color: homeColor.teal
            }}
          >
            {t("notifications.bell.viewAll", "View all")} →
          </button>
        }
      />

      <div
        className="flex-1 overflow-auto tailwind-scroll"
        id="notificationsScrollableDiv"
        ref={containerRef}
        style={{ maxHeight: HOME_LIST_MAX_HEIGHT }}
      >
        {loading ? (
          <div style={{ padding: 16 }}>
            <Skeleton active paragraph={{ rows: 3 }} />
          </div>
        ) : errored ? (
          <HomeNotice
            testId="home-notifications-error"
            title={t("notifications.bell.errorTitle", "Notifications could not load")}
            body={t(
              "notifications.bell.errorBody",
              "The connection dropped. Check back in a moment."
            )}
          />
        ) : notifications.length === 0 ? (
          <HomeNotice
            testId="home-notifications-empty"
            title={t("notifications.bell.empty")}
            body={t(
              "notifications.bell.emptyBody",
              "You are up to date. New activity on your leads and projects will appear here."
            )}
          />
        ) : (
          notifications.map((notification, index) => (
            <div
              key={notification.id}
              style={{
                borderTop:
                  index === 0 ? undefined : `1px solid ${homeColor.border}`
              }}
            >
              <NotificationContentCard
                content={notificationContent(notification)}
                timestamp={notification.timestamp}
                unread={notification.unread}
                level={notification.level}
                density="compact"
                fallbackText={notification.description}
                groupCount={notification.group_count}
                onOpen={() => openNotification(notification)}
                testId={`home-notification-${notification.id}`}
              />
            </div>
          ))
        )}
      </div>
    </section>
  )
}

function HomeNotice({
  title,
  body,
  testId
}: {
  title: string
  body: string
  testId: string
}) {
  return (
    <div data-testid={testId} style={{ padding: "32px 20px", textAlign: "center" }}>
      <p style={{ ...homeText.actionTitle, color: homeColor.ink, margin: "0 0 6px" }}>
        {title}
      </p>
      <p style={{ ...homeText.meta, color: homeColor.inkMuted, margin: 0 }}>{body}</p>
    </div>
  )
}
